import { useState } from "react";
import { ArrowUpRight, MapPin, Mail } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n";
import { api } from "@/api";
import { Reveal, Overline } from "@/components/motion";

const Contact = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-transparent border border-forest/20 rounded-full px-5 py-3.5 text-sm text-forest placeholder:text-sage/60 focus:outline-none focus:border-clay transition-colors duration-300";

  return (
    <div data-testid="contact-page" className="pt-24">
      <section className="max-w-[1500px] mx-auto px-6 md:px-12 pt-16 pb-32 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <Overline>{t("contact.overline")}</Overline>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter text-forest" data-testid="contact-title">
              {t("contact.title")}
            </h1>
            <p className="mt-8 text-base lg:text-lg leading-relaxed text-sage max-w-md">{t("contact.sub")}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-14 flex flex-col gap-8">
              <div className="flex items-start gap-4">
                <MapPin size={20} className="text-clay mt-1" />
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] font-semibold text-sage mb-1">{t("contact.addressLabel")}</p>
                  <p className="text-forest" data-testid="contact-address">{t("contact.address")}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Mail size={20} className="text-clay mt-1" />
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] font-semibold text-sage mb-1">{t("contact.emailLabel")}</p>
                  <a href="mailto:contact@future-gen.eu" data-testid="contact-email" className="text-forest hover:text-clay transition-colors duration-300">contact@future-gen.eu</a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.2}>
            <form onSubmit={submit} className="border border-forest/15 bg-sand/40 p-8 md:p-12 flex flex-col gap-5" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <input data-testid="contact-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("contact.name")} className={inputCls} />
                <input data-testid="contact-email-input" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t("contact.email")} className={inputCls} />
              </div>
              <input data-testid="contact-subject-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder={t("contact.subject")} className={inputCls} />
              <textarea
                data-testid="contact-message-input"
                required
                rows={7}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder={t("contact.message")}
                className="w-full bg-transparent border border-forest/20 rounded-3xl px-5 py-4 text-sm text-forest placeholder:text-sage/60 focus:outline-none focus:border-clay transition-colors duration-300 resize-none"
              />
              <button
                data-testid="contact-submit"
                disabled={loading}
                className="mt-2 inline-flex w-fit items-center gap-2 bg-forest text-cream px-10 py-4 rounded-full font-medium hover:bg-clay hover:-translate-y-0.5 transition-transform duration-300 disabled:opacity-60"
              >
                {t("contact.send")} <ArrowUpRight size={18} />
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
